import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Paper,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import { v4 as uuidv4 } from 'uuid';
import { doc, addDoc, collection, serverTimestamp, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { useUserManagement } from '../contexts/UserManagementContext';
import { useAuth } from '../contexts/AuthContext';
import { useBillings } from '../contexts/BillingContext';
import { db } from '../firebase';
import axios from 'axios';
import { API_BASE } from '../config';
import TransferOwnershipDialog from '../components/Dialogs/TransferOwnershipDialog';

// Roles that can be assigned/changed from the org-level UI.
// System / app-level roles like owner, appSupport, superAppadmin, billingManager
// are managed elsewhere (e.g., internal tools, ManagePermissions) and should
// not be editable via this dropdown.
const ASSIGNABLE_ROLES = ['viewer', 'editor', 'admin', 'billingManager'];

const UserManagementPage = () => {
  const { users, fetchUsersAndInvitations, setUsers, updateUserRole, transferOwnership } = useUserManagement();

  const {
    currentUser,
    organizationId,
    userProfile,
    hasPermission,
  } = useAuth();

  const { currentSubscription } = useBillings();

  const [localLoading, setLocalLoading] = useState(false);
  const [availableSeats, setAvailableSeats] = useState(0);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deletingUserId, setDeletingUserId] = useState(null); // Track the user being deleted
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferTargetUser, setTransferTargetUser] = useState(null);

  const canManageMembers =
    userProfile?.isOwner || hasPermission('manageUsers');

  useEffect(() => {
    const manageDataFetching = async () => {
      setLocalLoading(true);
      try {
        console.log('[UserManagementPage] Fetching data...');
        await fetchUsersAndInvitations();
        if (currentSubscription) {
          const totalSeats = currentSubscription.seats || 0;
          const usedSeats = users.length;
          setAvailableSeats(Math.max(totalSeats - usedSeats, 0));
        }
      } catch (error) {
        console.error('[UserManagementPage] Error fetching data:', error);
      } finally {
        setLocalLoading(false);
      }
    };

    if (organizationId) {
      manageDataFetching();
    } else {
      console.error('Error: Organization ID is required to fetch user management data.');
    }
  }, [fetchUsersAndInvitations, organizationId, currentSubscription, users.length]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError('');
  };

// ──────────────────────────────────────────────────────────────────────────────
//  sendInvitation()
//  – Creates or re-sends an invite
//  – Updates Firestore
//  – Sends the e-mail via the B-F-F
//  – Updates local state WITHOUT creating duplicates
// ──────────────────────────────────────────────────────────────────────────────
const sendInvitation = async (emailAddress) => {
  try {
    if (!organizationId) {
      throw new Error('Organization ID is required.');
    }

    const expiresInDays       = 7;
    const expirationTimestamp = new Date(
      Date.now() + expiresInDays * 24 * 60 * 60 * 1000
    );

    // ──────────────────────────────────────────────────────────────────
    // Check if an **active** invitation already exists for this e-mail/org
    // Only consider invites with status === 'sent' as "live".
    // Historical invites (inactive/accepted/expired) are ignored.
    // ──────────────────────────────────────────────────────────────────
    const invitationQuery = query(
      collection(db, 'userInvitations'),
      where('email', '==', emailAddress),
      where('organizationId', '==', organizationId),
      where('status', '==', 'sent') // ← NEW: only active "sent" invites
    );
    const querySnapshot = await getDocs(invitationQuery);

    let newInvitee;           // object that will be inserted / merged in state
    let invitationPayload;    // object sent to BFF for email

    if (!querySnapshot.empty) {
      // ────────────────────────────────────────────────────────────────
      // Re-send: update existing Firestore doc (same ID)
      // ────────────────────────────────────────────────────────────────
      const invitationDoc = querySnapshot.docs[0];
      const invitationRef = doc(db, 'userInvitations', invitationDoc.id);
      const existingData  = invitationDoc.data();

      await updateDoc(invitationRef, {
        expiresAt: expirationTimestamp,
        sentAt   : serverTimestamp(),
        status   : 'sent',
      });

      // Payload we send to the BFF – reuse existing metadata from Firestore,
      // but ensure we have fresh expiry + status.
      invitationPayload = {
        ...existingData,
        email         : emailAddress,
        organizationId,
        status        : 'sent',
        expiresAt     : expirationTimestamp,
      };

      newInvitee = {
        email            : emailAddress,
        role             : existingData.permissions || 'viewer',
        type             : 'invite',
        permissions      : existingData.permissions || 'viewer',
        invitationStatus : 'sent',
        id               : invitationDoc.id,
      };

      setSnackbar({
        open   : true,
        message: 'Invitation resent successfully.',
        severity: 'success',
      });
    } else {
      // ────────────────────────────────────────────────────────────────
      // Brand-new invitation → add Firestore doc
      // ────────────────────────────────────────────────────────────────
      const newInvitation = {
        email          : emailAddress,
        organizationId,
        invitedBy      : currentUser.uid,
        permissions    : 'viewer',
        token          : uuidv4(),
        status         : 'sent',
        expiresAt      : expirationTimestamp,
        sentAt         : serverTimestamp(),
      };

      const invitationRef = await addDoc(
        collection(db, 'userInvitations'),
        newInvitation
      );

      invitationPayload = newInvitation;

      newInvitee = {
        ...newInvitation,
        role            : 'viewer',
        type            : 'invite',
        invitationStatus: 'sent',
        id              : invitationRef.id,
      };

      setSnackbar({
        open   : true,
        message: 'Invitation sent successfully.',
        severity: 'success',
      });
    }

    // ──────────────────────────────────────────────────────────────────
    //  🔸 UPDATE LOCAL STATE without duplicating rows
    //     • If the e-mail is already present ⇒ replace existing entry
    //     • otherwise                       ⇒ push new entry
    // ──────────────────────────────────────────────────────────────────
    setUsers((prevUsers) => {
      const idx = prevUsers.findIndex(
        (u) => u.email.toLowerCase() === emailAddress.toLowerCase()
      );

      if (idx === -1) {
        // not present → append
        return [...prevUsers, newInvitee];
      }

      // already present → merge/replace
      const copy = [...prevUsers];
      copy[idx] = { ...copy[idx], ...newInvitee };
      return copy;
    });

    // ──────────────────────────────────────────────────────────────────
    //  ALWAYS send the e-mail (new invite OR resend)
    // ──────────────────────────────────────────────────────────────────
    try {
      const response = await axios.post(
        `${API_BASE}/send-invitations`,
        { invitations: [invitationPayload] }
      );
      console.log('Email invitations sent successfully:', response.data);
    } catch (apiError) {
      console.error('Error sending email invitations:', apiError);
      setSnackbar({
        open   : true,
        message: 'Failed to send email invitations. Please try again.',
        severity: 'error',
      });
    }
  } catch (error) {
    console.error('Error sending invitation:', error);
    setSnackbar({
      open   : true,
      message: 'Failed to send invitation. Please try again.',
      severity: 'error',
    });
  }
};

// ──────────────────────────────────────────────────────────────────────────────
//  handleResendInvite()
//  – Convenience wrapper to resend an invite for a given user row
//  – Delegates to sendInvitation(user.email)
// ──────────────────────────────────────────────────────────────────────────────
const handleResendInvite = async (user) => {
  try {
    // sendInvitation already handles:
    //  • checking organizationId
    //  • Firestore updates
    //  • Snackbar success/error messages
    await sendInvitation(user.email.toLowerCase());
  } catch (error) {
    console.error('[UserManagementPage] Error resending invite:', error);
    setSnackbar({
      open   : true,
      message: 'Failed to resend invitation. Please try again.',
      severity: 'error',
    });
  }
};


//   Centralised updates for accepted users come from UserManagementContext
//      (make sure you pulled `updateUserRole` from useUserManagement() at the top)
const handleRoleChange = async (user, newRole) => {
  try {
    // Special case: "Make owner" selection from Type dropdown
    if (user.type === 'user' && newRole === 'owner') {
      console.log(
        `[UserManagementPage] Make owner action selected for ${user.email} (${user.id})`
      );
      setTransferTargetUser(user);
      setTransferDialogOpen(true);
      return; // Do not fall through to updateUserRole
    }

    /* ────────────────────────────────────────────────────────────────
     * 1. Pending invitation → update userInvitations/{id}.permissions
     *    (No org-level validations needed yet.)
     * ──────────────────────────────────────────────────────────────── */
    if (user.type === 'invite') {
      console.log(
        `[UserManagementPage] Updating INVITE ${user.email} → ${newRole}`
      );

      const inviteRef = doc(db, 'userInvitations', user.id);
      await updateDoc(inviteRef, { permissions: newRole });

      // Optimistic local update: change both role & permissions field
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === user.id ? { ...u, role: newRole, permissions: newRole } : u
        )
      );
    }

    /* ────────────────────────────────────────────────────────────────
     * 2. Active account → delegate to context helper
     *    • validates owner / admin rules
     *    • writes BOTH organizations/* and accounts/* docs
     * ──────────────────────────────────────────────────────────────── */
    else {
      console.log(
        `[UserManagementPage] Updating USER ${user.email} → ${newRole}`
      );

      await updateUserRole(user.id, newRole); // from UserManagementContext

      // Optimistic local update: role only (permissions array lives in account doc)
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === user.id ? { ...u, role: newRole } : u
        )
      );
    }

    /* ────────────────────────────────────────────────────────────────
     * 3. Success snackbar
     * ──────────────────────────────────────────────────────────────── */
    setSnackbar({
      open   : true,
      message: `Role updated to ${newRole} for ${user.email}.`,
      severity: 'success',
    });
  } catch (error) {
    /* ────────────────────────────────────────────────────────────────
     * 4. Error handling
     * ──────────────────────────────────────────────────────────────── */
    console.error('[UserManagementPage] Error updating role:', error);

    setSnackbar({
      open   : true,
      message: error.message || 'Failed to update role. Please try again.',
      severity: 'error',
    });
  }
};




const handleDeleteUser = async (user) => {
  try {
    if (!organizationId) {
      throw new Error('Organization ID is required.');
    }

    // Set the user being deleted for spinner feedback
    setDeletingUserId(user.id);
    console.log(`[UserManagementPage] Attempting to delete user with ID: ${user.id} and type: ${user.type}`);

    // Prepare the payload based on the user type
    const payload = user.type === 'invite'
      ? { invitationId: user.id, organizationId, isInvite: true }
      : { userId: user.id, organizationId };

    // Log details about invited users
    if (user.type === 'invite') {
      console.log(`[UserManagementPage] Deleting invitation for email: ${user.email}`);
      if (user.invitationStatus === 'expired') {
        console.warn(`[UserManagementPage] Invitation for ${user.email} is already expired.`);
      }
    }

    // Call the delete-account endpoint
    const response = await axios.post(`${API_BASE}/api/delete-account`, payload);

    if (response.status === 200) {
      console.log(`[UserManagementPage] ${user.type === 'invite' ? 'Invitation' : 'User'} ${user.id} deleted successfully.`);

      // Update local state to remove the user
      setUsers((prevUsers) => {
        const updatedUsers = prevUsers.filter((u) => u.id !== user.id);
        if (updatedUsers.length === prevUsers.length) {
          console.warn(`[UserManagementPage] User with ID ${user.id} not found in local state.`);
        }
        return updatedUsers;
      });

      setSnackbar({
        open: true,
        message: `${user.type === 'invite' ? 'Invitation for' : ''} ${user.email} has been removed successfully.`,
        severity: 'success',
      });
    } else {
      console.error(`[UserManagementPage] Failed to delete ${user.type === 'invite' ? 'invitation' : 'user'}.`, {
        status: response.status,
        data: response.data,
      });
      setSnackbar({
        open: true,
        message: `Failed to delete ${user.type === 'invite' ? 'invitation' : 'user'}. Please try again.`,
        severity: 'error',
      });
    }
  } catch (error) {
    console.error(`[UserManagementPage] Error deleting ${user.type === 'invite' ? 'invitation' : 'user'}:`, error);

    // Show a detailed error message if available
    const errorMessage =
      error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : 'An unknown error occurred. Please try again.';

    setSnackbar({
      open: true,
      message: `Failed to delete ${user.type === 'invite' ? 'invitation' : 'user'}. ${errorMessage}`,
      severity: 'error',
    });
  } finally {
    // Clear the deleting state to restore the delete icon
    setDeletingUserId(null);
  }
};


const handleAddEmail = async () => {
  const trimmedEmail = email.trim().toLowerCase();

  // 1️⃣ basic format checks
  if (!trimmedEmail) return;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
    setEmailError('Invalid email format');
    return;
  }

  // 2️⃣ NEW → is this e-mail already in users/invites?
  const alreadyExists = users.some(
    (u) => u.email.toLowerCase() === trimmedEmail
  );

  if (alreadyExists) {
    setEmailError('This email is already part of your organization');
    return;
  }

  // 3️⃣ proceed to create / resend invitation
  try {
    await sendInvitation(trimmedEmail);
    setEmail('');               // clear input
  } catch (error) {
    console.error('[UserManagementPage] Error adding email:', error);
  }
};


const handleSnackbarClose = (event, reason) => {
  if (reason === 'clickaway') return;
  setSnackbar({ open: false, message: '', severity: 'info' });
};

const handleConfirmTransferOwnership = async () => {
  if (!transferTargetUser) return;

  try {
    await transferOwnership(transferTargetUser.id);
    setSnackbar({
      open   : true,
      message: `Ownership transferred to ${transferTargetUser.email}.`,
      severity: 'success',
    });
  } catch (error) {
    console.error('[UserManagementPage] Error in transferOwnership confirm:', error);
    setSnackbar({
      open   : true,
      message: error.message || 'Failed to transfer ownership. Please try again.',
      severity: 'error',
    });
  } finally {
    setTransferDialogOpen(false);
    setTransferTargetUser(null);
  }
};

const handleCancelTransferOwnership = () => {
  setTransferDialogOpen(false);
  setTransferTargetUser(null);
};

// ──────────────────────────────────────────────────────────────────────────────
//  getUserActivityLabel()
//  – Returns a compact, human-readable activity label for **non-invite** users.
//  – Uses lastLoginAt when available, otherwise falls back to createdAt.
//  – Designed for an "Activity Status" column: Today, Yesterday, 3 days ago,
//    2 weeks ago, Dec 5, etc. (no "Last active" / "Joined" prefix).
// ──────────────────────────────────────────────────────────────────────────────
const getUserActivityLabel = (user) => {
  // Firestore Timestamps need to be converted via .toDate()
  const toJsDate = (maybeTs) => {
    if (!maybeTs) return null;
    if (maybeTs.toDate && typeof maybeTs.toDate === 'function') {
      return maybeTs.toDate();
    }
    // Fallback if it’s already a Date or ISO string
    return new Date(maybeTs);
  };

  const lastLoginAt = toJsDate(user.lastLoginAt);
  const createdAt   = toJsDate(user.createdAt);

  const now = new Date();

  const diffInDays = (d) => {
    const msPerDay = 1000 * 60 * 60 * 24;
    const diffMs   = now - d;
    return Math.floor(diffMs / msPerDay);
  };

  const formatShortDate = (d) =>
    d
      ? d.toLocaleDateString(undefined, {
          month: 'short',
          day  : 'numeric',
        }) // e.g., "Dec 5"
      : null;

  // Helper to get a compact "X days ago" style string
  const formatRelative = (d) => {
    const days = diffInDays(d);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) {
      const weeks = Math.round(days / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    }
    // Older than ~1 month → just show a short date
    return formatShortDate(d);
  };

  // Prefer lastLoginAt as the primary signal of activity
  if (lastLoginAt) {
    return formatRelative(lastLoginAt);
  }

  // If they've never logged in (or we don't have that yet), fall back
  // to when the account was created; treat it as a "joined" date, but
  // show it as a compact token like "Dec 5".
  if (createdAt) {
    return formatShortDate(createdAt) || 'No recent activity';
  }

  // Ultimate fallback if we truly have no timestamps
  return 'No recent activity';
};

// ──────────────────────────────────────────────────────────────────────────────
//  getInviteActivityLabel()
//  – Maps invitation status values to friendly labels for the table.
//  – Ensures we show "Invite sent" instead of raw "sent", etc.
// ──────────────────────────────────────────────────────────────────────────────
const getInviteActivityLabel = (user) => {
  const rawStatus = (user.invitationStatus || user.status || '').toLowerCase();

  switch (rawStatus) {
    case 'sent':
      return 'Invite sent';
    case 'expired':
      return 'Invite expired';
    case 'accepted':
      return 'Joined';
    case 'inactive':
      return 'Invite cancelled';
    default:
      // If we somehow have no status yet but it's an invite, treat it as pending
      return 'Invite pending';
  }
};




  if (localLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }



  return (
    <Paper sx={{ width: '100%', p: 3, minHeight: '400px' }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        Members
      </Typography>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Available Seats: {availableSeats}
      </Typography>

      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <TextField
          label="Invite Members"
          placeholder="Enter email"
          value={email}
          onChange={handleEmailChange}
          onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
          error={!!emailError}
          helperText={emailError || ''}
          sx={{ flex: 1 }}
        />
        <Button onClick={handleAddEmail} variant="contained">
          Add
        </Button>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle1" fontWeight="bold">
                Name
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1" fontWeight="bold">
                Type
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1" fontWeight="bold">
                Activity Status
              </Typography>
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user, index) => {
            const isOwnerSelfRow =
              user.role === 'owner' && currentUser?.uid === user.id;

            return (
              <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell sx={{ py: 1 }}>
                  <Typography variant="body2">
                    <strong>{user.firstName || ''} {user.lastName || ''}</strong>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {user.email}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  {canManageMembers &&
                   ASSIGNABLE_ROLES.includes(user.role || 'viewer') ? (
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none',
                          },
                        },
                        '& .MuiSelect-select': {
                          fontSize: '0.875rem',
                          padding: '0 8px',
                        },
                      }}
                    >
                      <Select
                        value={user.role || 'viewer'}
                        onChange={(e) => handleRoleChange(user, e.target.value)}
                        displayEmpty
                        inputProps={{
                          style: {
                            padding: 0,
                          },
                        }}
                      >
                        <MenuItem value="viewer" sx={{ fontSize: '0.875rem' }}>
                          Viewer
                        </MenuItem>
                        <MenuItem value="editor" sx={{ fontSize: '0.875rem' }}>
                          Editor
                        </MenuItem>
                        <MenuItem value="admin" sx={{ fontSize: '0.875rem' }}>
                          Admin
                        </MenuItem>
                        <MenuItem value="billingManager" sx={{ fontSize: '0.875rem' }}>
                          Billing Manager
                        </MenuItem>

                        {/* Owner-only: "Make owner" action for other users with real accounts */}
                        {userProfile?.isOwner &&
                          currentUser?.uid &&
                          user.type === 'user' &&
                          user.id !== currentUser.uid && (
                            <>
                              <MenuItem
                                disabled
                                sx={{ fontSize: '0.75rem', opacity: 0.7 }}
                              >
                                — Owner actions —
                              </MenuItem>
                              <MenuItem value="owner" sx={{ fontSize: '0.875rem' }}>
                                Make owner
                              </MenuItem>
                            </>
                          )}

                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body2">
                      {user.role}
                    </Typography>
                  )}
                </TableCell>

                <TableCell sx={{ py: 1 }}>
                  {user.type === 'invite' ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="body2"
                        color={
                          (user.invitationStatus || user.status) === 'expired'
                            ? 'error'
                            : 'inherit'
                        }
                      >
                        {getInviteActivityLabel(user)}
                      </Typography>

                      {/* Resend icon for invites (sent or expired) */}
                      <Tooltip title="Re-send invite email">
                        <IconButton
                          size="small"
                          aria-label="Re-send invite email"
                          onClick={() => handleResendInvite(user)}
                          sx={{ p: 0.5 }}
                        >
                          <ReplayIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      {getUserActivityLabel(user)}
                    </Typography>
                  )}
                </TableCell>

                <TableCell sx={{ py: 1 }}>
                  <IconButton
                    onClick={() => handleDeleteUser(user)}
                    aria-label="delete"
                    size="small"
                    color="error"
                    disabled={
                      deletingUserId === user.id ||
                      isOwnerSelfRow // Prevent owner from deleting their own account
                    }
                    sx={{ p: 0.5 }}
                  >
                    {deletingUserId === user.id ? (
                      <CircularProgress size={14} /> // Show spinner while deleting
                    ) : (
                      <DeleteIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                    )}
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Snackbar
        open={snackbar.open}
        onClose={handleSnackbarClose}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <TransferOwnershipDialog
        open={transferDialogOpen}
        onClose={handleCancelTransferOwnership}
        onConfirm={handleConfirmTransferOwnership}
        targetUser={transferTargetUser}
      />

    </Paper>
  );
};

export default UserManagementPage;
